import React, { useState, useEffect } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import {
  Grid,
  TextField,
  Box,
  Select,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogContent,
  Typography,
  Paper,
  Autocomplete,
  IconButton,
} from "@mui/material";
import {
  StyledTypography,
  StyledButton,
  StyledAlert,
  StyledSnackbar,
  StyledAgGridContainer,
  StyledTableContainer,
} from "components/StyledComponents/styledComponents.js";
import { useMaterialUIController } from "context";
import URLs from "constants/urls";
import Cookie from "js-cookie";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ResponsiveTabs from "components/StyledComponents/styledComponents";

export default function Agents() {
  const [controller] = useMaterialUIController();
  const { user } = controller;
  const hrms_token = Cookie.get("hrms_token");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [agents, setAgents] = useState([]);
  const [message, setMessage] = useState("");
  const [alertType, setAlertType] = useState("success");
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
  });

  const tabs = [{ label: "Agent List" }, { label: "Agent Registration" }];

  const setAlert = (msg, type) => {
    setMessage(msg);
    setAlertType(type);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (activeTab === 0) {
      fetchAgents();
    }
  }, [activeTab]);

  const fetchAgents = async () => {
    setLoading(true);
    try {
      const response = await fetch(URLs.hrmsGetAgentsUrl, {
        headers: {
          Authorization: `Bearer ${hrms_token}`,
        },
      });
      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }
      const data = await response.json();
      setAgents(data);
    } catch (error) {
      setAlert(`Failed to fetch agents: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleSubmit = async () => {
    if (!formData.firstName || !formData.lastName) {
      setAlert("Please fill in all required fields", "error");
      return;
    }

    const currentDate = new Date().toISOString().split("T")[0];

    const payload = {
      employeeId: 0,
      employeeCode: " ",

      dateOfJoining: currentDate,
      dateOfBirth: currentDate,
      address: {
        streetName: "",
        cityName: "",
        districtName: "",
        stateName: "",
        zipCode: "",
      },
      phoneNumber: " ",
      emailId: " ",
      alternatePhoneNumber: " ",
      reference: " ",
      role: {
        roleId: 0,
        roleName: "Agent",
      },
      ctcPerYear: 0,
      applicationAccess: "N",
      companyId: user.companyId,
      activeStatus: "Y",
      payRollStatus: "N",
      applicantId: 0,
      photoAttachment: " ",
      password: " ",
      firstName: formData.firstName,
      lastName: formData.lastName,
    };

    setLoading(true);
    try {
      const response = await fetch("http://185.193.19.156:8092/fintrack/employeeDetails", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${hrms_token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Registration failed: ${response.status}`);
      }

      setAlert("Agent registered successfully", "success");
      setFormData({ firstName: "", lastName: "" });
      setActiveTab(0);
      fetchAgents();
    } catch (error) {
      setAlert(`Registration failed: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      headerName: "Agent Name",
      field: "agentName",
      valueGetter: (params) =>
        `${params.data.firstName}${params.data.lastName ? " " + params.data.lastName : ""}`,
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: "Agent ID",
      field: "agentId",
      sortable: true,
      filter: true,
      flex: 1,
    },
  ];

  return (
    <DashboardLayout>
      <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", mt: 0 }}>
        <ResponsiveTabs
          tabs={tabs}
          activeTab={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          labelColor="secondary"
          bgColor="#e0f7fa"
        />

        <StyledSnackbar
          open={open}
          autoHideDuration={5000}
          onClose={handleClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <StyledAlert onClose={handleClose} severity={alertType}>
            {message}
          </StyledAlert>
        </StyledSnackbar>

        {activeTab === 0 ? (
          <Box sx={{ mt: 2 }}>
            <StyledAgGridContainer
              component={Paper}
              className="ag-theme-alpine"
              sx={{
                flexGrow: 1,
                backgroundColor: "transparent",
                height: "calc(100vh - 200px)",
                width: "100%",
              }}
            >
              {loading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : (
                <AgGridReact
                  rowData={agents}
                  columnDefs={columns}
                  pagination={true}
                  paginationPageSize={10}
                  defaultColDef={{
                    resizable: true,
                    filter: true,
                  }}
                  overlayNoRowsTemplate="<span>No agents found</span>"
                />
              )}
            </StyledAgGridContainer>
          </Box>
        ) : (
          <Box sx={{ mt: 2, width: "100%" }}>
            <Paper
              sx={{
                display: "flex",
                backgroundColor: "#ffffff",
                minHeight: "200px",
                borderRadius: "15px",
                overflow: "auto",
                width: "100%",
                p: 3,
              }}
            >
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="First Name"
                    value={formData.firstName}
                    onChange={handleInputChange("firstName")}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Last Name"
                    value={formData.lastName}
                    onChange={handleInputChange("lastName")}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <StyledButton onClick={handleSubmit} disabled={loading} variant="contained">
                    {loading ? "Registering..." : "Register Agent"}
                  </StyledButton>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        )}

        <Dialog open={loading} aria-labelledby="loading-dialog-title">
          <DialogContent style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <CircularProgress color="primary" />
            <Typography variant="h6" style={{ marginTop: "20px" }}>
              Loading, please wait...
            </Typography>
          </DialogContent>
        </Dialog>
      </Box>
    </DashboardLayout>
  );
}
